import React, { useState, useRef, useEffect } from "react";
import "../style.css";
import {
  ActionButton,
  Flex,
  ProgressCircle,
  TextArea,
} from "@adobe/react-spectrum";
import { AttachIcon, CannedResponsesIcon, SendIcon } from "../../common/Icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import Emitter from "../../../emitter";
import {
  API_SENDCHATMESSAGE,
  API_SENDCHATTYPING,
  SHOW_SELECTED_ATTACHMENT,
} from "../../../constants/events";
import { IndividualChat } from "../../../types/chat.type";
import {
  AGENT_MESSAGE_LENGTH,
  ALLOWED_FILE_TYPE,
  API_SEND_ATTACHMENT,
  FILE_TYPE_NOT_SUPPORTED,
  MAX_FILE_SIZE_MESSAGE,
  MAX_SIZE_IN_MB,
  SHOW_UPLOADING_ATTACHMENT,
  UPDATE_AGENT_RESPONSE,
} from "../../../constants/constants";
import CannedResponseModal from "./CannedResponseModal";
import { Toast } from "../../common/Toast";
import storeRTK from "../../../store/storeRTK";
import { unsendChatMessage } from "../../../actions/chat";

interface IChatFooterProps {
  uplodingFiles: any;
  setUploadingFiles: (files: any) => void;
  activeChatId: string;
}

const ChatFooter: React.FC<IChatFooterProps> = (props: IChatFooterProps) => {
  const { activeChatId, uplodingFiles, setUploadingFiles } = props;
  const [chatMessage, setChatMessage] = useState<Record<string, string>>({});
  const [chatText, setChatText] = useState<string>("");
  const [chatTyping, setChatTyping] = useState(false);
  const [openCannedModal, setOpenCannedModal] = useState(false);
  const [cannedResponse, setCannedResponse] = useState("");
  const [isFileuploading, setIsFileUploading] = useState(false);
  const [fileSizeError, setFileSizeError] = useState("");

  const chat = useSelector((state: RootState) => state.chat);
  const activeChat = chat.chats.find(
    (ch: IndividualChat) => ch.id === activeChatId
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setChatText(activeChat?.draftChatMsg || "");
    setChatMessage({
      ...chatMessage,
      [chat.activeChatID]:
        typeof activeChat?.draftChatMsg === "string" &&
        activeChat?.draftChatMsg[activeChat?.draftChatMsg.length - 1] !== " "
          ? activeChat?.draftChatMsg.trim()
          : activeChat?.draftChatMsg,
    });
  }, [activeChatId]);

  useEffect(() => {
    if (fileSizeError) {
      const timer = setTimeout(() => {
        setFileSizeError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [fileSizeError]);

  useEffect(() => {
    if (cannedResponse?.length > 0) {
      setChatText(cannedResponse);
      changeChatMessage(cannedResponse);
    }
  }, [cannedResponse]);

  const clearAllTextInput = () => {
    setChatText("");
    setCannedResponse("");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFileUploading(true);
    const file = e.target.files?.[0];
    const isFileSizeValid = file && checkFileSize(file, MAX_SIZE_IN_MB);

    if (!file || !isFileSizeValid) {
      setFileSizeError(MAX_FILE_SIZE_MESSAGE);
      clearSelectedFile();
      return;
    }

    const allowedExtensions = ALLOWED_FILE_TYPE;
    const fileExtension = getFileExtension(file.name).toLowerCase();
    if (allowedExtensions.includes(fileExtension)) {
      try {
        const base64Data = await fileToBase64(file);
        Emitter.emit(SHOW_SELECTED_ATTACHMENT, {
          body: {
            attachment: {
              attachmentName: file.name,
              contentType: file.type,
              attachment: base64Data,
            },
            conversationId: activeChat?.id,
          },
        });
      } catch (error) {
        console.error("Error converting file to Base64:", error);
        clearSelectedFile();
      }
    } else {
      setFileSizeError(FILE_TYPE_NOT_SUPPORTED);
      clearSelectedFile();
    }
  };

  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleSendAttachment = () => {
    const activeChat = chat?.chats.find(
      (ele: IndividualChat) => ele.id === chat.activeChatID
    );

    const file = activeChat?.attachment?.attachment;
    const attachmentName = activeChat?.attachment?.attachmentName;
    const contentType = activeChat?.attachment?.contentType;
    const uploadingFiles = activeChat?.uploadingFiles || [];

    if (file.length > 0) {
      const fileData = { attachmentName, contentType };
      if (uplodingFiles.hasOwnProperty(activeChat?.id)) {
        setUploadingFiles((prev: any) => ({
          ...prev,
          [activeChat?.id]: [...prev[activeChat?.id], fileData],
        }));
      } else {
        setUploadingFiles((prev: any) => ({
          ...prev,
          [activeChat?.id]: [fileData],
        }));
      }

      Emitter.emit(SHOW_UPLOADING_ATTACHMENT, {
        body: {
          attachment: [...uploadingFiles, fileData],
          conversationId: activeChat?.id,
        },
      });

      Emitter.emit(API_SEND_ATTACHMENT, {
        body: {
          attachment: file,
          conversationId: activeChat?.id,
          attachmentName,
          contentType,
        },
      });

      clearSelectedFile();
    } else {
      clearSelectedFile();
    }
  };

  function checkFileSize(file: File, maxSizeInMB: number) {
    return file.size / (1024 * 1024) <= maxSizeInMB;
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (event: ProgressEvent<FileReader>) {
        const base64String = event?.target?.result as string;
        const base64Data = base64String?.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function getFileExtension(filename: string) {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
  }

  const sendMessageOnEnter = (e: React.KeyboardEvent) => {
    const selectedFile =
      chat?.chats[
        chat?.chats.findIndex(
          (ele: IndividualChat) => ele.id === chat.activeChatID
        )
      ]?.attachment?.attachment;

    if (e.key === "Enter") {
      if (selectedFile) {
        handleSendAttachment();
      }
      pressSendButton();
    }
  };

  const sendMessageOnSendButton = () => {
    const selectedFile =
      chat?.chats[
        chat?.chats.findIndex((ele: any) => ele.id === chat.activeChatID)
      ]?.attachment?.attachment;
    if (selectedFile) {
      handleSendAttachment();
    }
    pressSendButton();
  };

  function clearSelectedFile() {
    setIsFileUploading(false);
    Emitter.emit(SHOW_SELECTED_ATTACHMENT, {
      body: {
        attachment: {
          attachmentName: "",
          contentType: "",
          attachment: null,
        },
        conversationId: activeChat?.id,
      },
    });
  }

  const pressSendButton = () => {
    if (
      chatMessage[chat.activeChatID] &&
      chatMessage[chat.activeChatID].trim() !== ""
    ) {
      Emitter.emit(API_SENDCHATMESSAGE, {
        body: {
          communicationId:
            activeChat.self.senderId || activeChat.self?.attributes?.senderId,
          contactId: activeChat.id,
          message: chatText,
        },
      });

      setChatMessage({ ...chatMessage, [chat.activeChatID]: "" });
      storeRTK.dispatch(unsendChatMessage(chat.activeChatID, ""));
      clearAllTextInput();
      Emitter.emit(UPDATE_AGENT_RESPONSE, {
        body: {
          body: { id: chat.activeChatID, value: "" },
        },
      });
    }
  };

  const changeChatMessage = (value: string) => {
    storeRTK.dispatch(unsendChatMessage(chat.activeChatID, value));
    setChatMessage({
      ...chatMessage,
      [chat.activeChatID]:
        typeof value === "string" && value[value.length - 1] !== " "
          ? value.trim()
          : value,
    });
    sendChatTyping();
    setChatText(value);
    if (value.trim() !== "") {
      Emitter.emit(UPDATE_AGENT_RESPONSE, {
        body: {
          body: { id: chat.activeChatID, value },
        },
      });
    }
  };

  const sendChatTyping = () => {
    if (chatTyping) return;
    setChatTyping(true);
    Emitter.emit(API_SENDCHATTYPING, {
      body: {
        communicationId:
          activeChat.self.senderId || activeChat.self?.attributes?.senderId,
        contactId: activeChat.id,
      },
    });

    setTimeout(() => {
      setChatTyping(false);
    }, 5000);
  };

  return (
    <Flex
      alignItems={"center"}
      UNSAFE_className="chat-footer"
      direction={"column"}
    >
      {fileSizeError && (
        <Flex marginTop={"size-100"} marginBottom={"size-100"}>
          <Toast message={fileSizeError} variant={"negative"} duration={5000} />
        </Flex>
      )}

      <Flex width={"100%"} alignItems={"center"}>
        {chat?.chats[
          chat?.chats.findIndex((ele: any) => ele.id === chat.activeChatID)
        ]?.attachment?.attachment ? (
          <Flex direction={"row"} alignItems={"center"}>
            <ActionButton
              type="button"
              UNSAFE_className={"fileUpload fileButtonText"}
              marginStart={"size-300"}
              marginTop={"size-100"}
            >
              <span className="fileUploadText">
                {
                  chat?.chats[
                    chat?.chats.findIndex(
                      (ele: IndividualChat) => ele.id === chat.activeChatID
                    )
                  ]?.attachment?.attachmentName
                }
              </span>
              <span className="cancelImageUpload" onClick={clearSelectedFile}>
                x
              </span>
            </ActionButton>
          </Flex>
        ) : isFileuploading ? (
          <Flex marginStart={"size-300"} marginTop={"size-100"}>
            <ProgressCircle
              size="S"
              aria-label="Loading…"
              isIndeterminate
              marginStart="size-100"
            />
            <span className="uploadingDocs">Uploading ...</span>
          </Flex>
        ) : null}
      </Flex>

      <Flex width={"100%"} alignItems={"center"} position="relative">
        <div style={{ position: "relative", width: "100%" }}>
          <TextArea
            placeholder="Type a message"
            width="100%"
            isQuiet
            value={chatText}
            onChange={changeChatMessage}
            UNSAFE_style={chatText.length > 100 ? { height: "100px" } : { maxHeight: "100px" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessageOnEnter(e);
              }
            }}
            maxLength={AGENT_MESSAGE_LENGTH}
            UNSAFE_className="chat-textfield scrollable-textarea"
          />
          <span className="message-length-counter">
            {chatMessage[chat.activeChatID]
              ? `${
                  chatMessage[chat.activeChatID]?.length
                }/${AGENT_MESSAGE_LENGTH}`
              : `0/${AGENT_MESSAGE_LENGTH}`}
          </span>
        </div>
        <div onClick={handleAttachmentClick} style={{ cursor: "pointer" }}>
          <AttachIcon />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
        <div onClick={() => setOpenCannedModal(true)}>
          <CannedResponsesIcon />
        </div>
        {openCannedModal && (
          <CannedResponseModal
            setOpenCannedModal={setOpenCannedModal}
            setCannedResponse={setCannedResponse}
          />
        )}
        <span onClick={sendMessageOnSendButton} className="chat-send-icon">
          <SendIcon />
        </span>
      </Flex>
    </Flex>
  );
};

export default ChatFooter;
